import { memo, useState, type MouseEvent, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canViewTicketTranscript, getTicketsxTranscriptUrl, parseTicketId } from '../utils/ticketsx';
import { cn } from '../utils/cn';
import { TicketTranscriptModal } from './TicketTranscriptModal';

interface TicketIdLinkProps {
  ticketId: number | string;
  className?: string;
  children?: ReactNode;
}

const LINK_CLASSES = 'text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 tabular-nums transition-colors';

export const TicketIdLink = memo(({ ticketId, className, children }: TicketIdLinkProps) => {
  const { identity } = useAuth();
  const [open, setOpen] = useState(false);
  const parsedId = parseTicketId(ticketId);
  const canPreview = parsedId !== null && canViewTicketTranscript(identity);

  if (!canPreview) {
    return (
      <a
        href={getTicketsxTranscriptUrl(ticketId)}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(LINK_CLASSES, className)}
        title="Open ticket on TicketsX"
      >
        {children ?? `#${ticketId}`}
      </a>
    );
  }

  const handleOpen = (e: MouseEvent) => {
    // Ticket links live inside clickable rows/cards — don't trigger those.
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={cn('text-left', LINK_CLASSES, className)}
        title="Preview ticket transcript"
      >
        {children ?? `#${ticketId}`}
      </button>
      {open && <TicketTranscriptModal ticketId={parsedId} onClose={() => setOpen(false)} />}
    </>
  );
});

TicketIdLink.displayName = 'TicketIdLink';
